// Date + status helpers — ported from code.gs (v1)
// Pure functions only, no Apps Script dependencies.
import { JOB_STATUS_AWAITING, JOB_STATUS_COMPLETED, JOB_STATUS_CANCELLED, JOB_TYPE_PERSONAL, PERSONAL_JOB_TAG, PAYMENT_DUE_AUTO_MONTHS, PAYMENT_DUE_LINE_REMINDER_MAX_OVERDUE_MONTHS, } from './constants.js';
export function isPersonalJob(job) {
    if (!job)
        return false;
    const jobType = String(job.JobType ?? '').trim();
    if (jobType === JOB_TYPE_PERSONAL)
        return true;
    const notes = String(job.Notes ?? '').trim();
    return notes.includes(PERSONAL_JOB_TAG);
}
export function normalizeJobStatus(status) {
    const raw = String(status ?? '').trim();
    if (!raw)
        return JOB_STATUS_AWAITING;
    if (raw === JOB_STATUS_AWAITING ||
        raw === JOB_STATUS_COMPLETED ||
        raw === JOB_STATUS_CANCELLED) {
        return raw;
    }
    const norm = raw.toLowerCase();
    if (norm === 'pending' || norm === 'confirmed' || norm === 'awaitingpayment') {
        return JOB_STATUS_AWAITING;
    }
    if (norm === 'completed' ||
        norm === 'complete' ||
        norm === 'complate' ||
        norm === 'paid' ||
        norm === 'เสร็จงาน' ||
        norm === 'ชำระเงินแล้ว') {
        return JOB_STATUS_COMPLETED;
    }
    if (norm === 'cancelled' || norm === 'canceled')
        return JOB_STATUS_CANCELLED;
    if (raw.indexOf('รอ') !== -1 || raw.indexOf('ชำระ') !== -1)
        return JOB_STATUS_AWAITING;
    if (raw.indexOf('เสร็จ') !== -1)
        return JOB_STATUS_COMPLETED;
    if (raw.indexOf('ยกเลิก') !== -1)
        return JOB_STATUS_CANCELLED;
    return JOB_STATUS_AWAITING;
}
export function isAwaitingPaymentStatus(status) {
    return normalizeJobStatus(status) === JOB_STATUS_AWAITING;
}
export function normalizeEventDateRaw(value) {
    let raw = String(value ?? '').trim();
    if (!raw)
        return '';
    raw = raw.replace(/\r?\n+/g, ' ').replace(/\s+/g, ' ').trim();
    const dashRange = raw.match(/^(\d{1,2}\/\d{1,2}\/\d{4})\s*-\s*(\d{1,2}\/\d{1,2}\/\d{4})$/);
    if (dashRange)
        return dashRange[1] + ' to ' + dashRange[2];
    const compactSameMonth = raw.match(/^(\d{1,2})-(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (compactSameMonth) {
        return (compactSameMonth[1] + '/' + compactSameMonth[3] + '/' + compactSameMonth[4] +
            ' to ' + compactSameMonth[2] + '/' + compactSameMonth[3] + '/' + compactSameMonth[4]);
    }
    if (raw.indexOf(' to ') === -1 && raw.indexOf(' - ') !== -1) {
        raw = raw.replace(/\s+-\s+/g, ' to ');
    }
    return raw;
}
// Parses one already-normalized single-date token ("dd/MM/yyyy" or "yyyy-MM-dd",
// falling back to native Date parsing). Does not handle ranges or lists.
// Also accepts Google Sheets serial day numbers (UNFORMATTED_VALUE), e.g. 46225
// = 22/07/2026 — without this, `new Date("46225")` becomes year 46225 and
// Calendar sync fails with "Invalid start time".
function sheetsSerialToDate(serial) {
    if (!Number.isFinite(serial))
        return null;
    // Plausible civil-date window for this app (~1954–2119).
    if (serial < 20000 || serial > 80000)
        return null;
    const ms = Date.UTC(1899, 11, 30) + Math.floor(serial) * 86400000;
    const utc = new Date(ms);
    if (isNaN(utc.getTime()))
        return null;
    // Civil date from UTC components — Workers run in UTC; constructing via
    // local y/m/d keeps parseEventDateList day arithmetic stable.
    return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
}
function looksLikeSheetsSerial(raw) {
    return /^\d{5}(?:\.\d+)?$/.test(raw.trim());
}
function parseSingleDateToken(token) {
    const raw = token.trim();
    if (!raw)
        return null;
    // Sheets serial (number-like) — must run before `new Date("46226")`.
    if (looksLikeSheetsSerial(raw)) {
        const serialDate = sheetsSerialToDate(Number(raw));
        if (serialDate)
            return serialDate;
    }
    const ddmmyyyy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4,5})$/);
    if (ddmmyyyy) {
        const day = Number(ddmmyyyy[1]);
        const month = Number(ddmmyyyy[2]);
        const year = Number(ddmmyyyy[3]);
        // Repair rows corrupted by serial→year misparse. `new Date("46225")` becomes
        // Jan 1 of year 46225; later addMonths (payment due) shifts month/day while
        // keeping the bogus year — recover by treating year as the Sheets serial,
        // then re-applying the civil offset from Jan 1.
        if (year >= 20000 && year <= 80000) {
            const base = sheetsSerialToDate(year);
            if (base) {
                const repaired = new Date(base.getFullYear(), base.getMonth(), base.getDate());
                repaired.setMonth(repaired.getMonth() + (month - 1));
                repaired.setDate(repaired.getDate() + (day - 1));
                return repaired;
            }
        }
        if (year < 1990 || year > 2100)
            return null;
        return new Date(year, month - 1, day);
    }
    const yyyymmdd = raw.match(/^(\d{4,5})-(\d{1,2})-(\d{1,2})$/);
    if (yyyymmdd) {
        const year = Number(yyyymmdd[1]);
        const month = Number(yyyymmdd[2]);
        const day = Number(yyyymmdd[3]);
        if (year >= 20000 && year <= 80000) {
            const base = sheetsSerialToDate(year);
            if (base) {
                const repaired = new Date(base.getFullYear(), base.getMonth(), base.getDate());
                repaired.setMonth(repaired.getMonth() + (month - 1));
                repaired.setDate(repaired.getDate() + (day - 1));
                return repaired;
            }
        }
        if (year < 1990 || year > 2100)
            return null;
        return new Date(year, month - 1, day);
    }
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime()))
        return null;
    const year = parsed.getFullYear();
    if (year < 1990 || year > 2100)
        return null;
    return parsed;
}
// Explicit comma-separated multi-date list (new "select scattered days" format),
// e.g. "01/06/2026,02/06/2026,05/06/2026". Returns parsed dates, unsorted/undeduped.
function parseEventDateTokens(rawValue) {
    return rawValue
        .split(',')
        .map((token) => parseSingleDateToken(normalizeEventDateRaw(token)))
        .filter((d) => !!d);
}
export function parseEventDateStart(value) {
    if (!value && value !== 0)
        return null;
    if (Object.prototype.toString.call(value) === '[object Date]') {
        const d = value;
        return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        const serialDate = sheetsSerialToDate(value);
        if (serialDate)
            return serialDate;
    }
    const rawValue = String(value ?? '').trim();
    if (rawValue.includes(',')) {
        const dates = parseEventDateTokens(rawValue);
        if (!dates.length)
            return null;
        return dates.reduce((min, d) => (d.getTime() < min.getTime() ? d : min));
    }
    const raw = normalizeEventDateRaw(rawValue);
    const firstPart = raw.includes(' to ') ? raw.split(' to ')[0].trim() : raw;
    return parseSingleDateToken(firstPart);
}
// Overall bounding window [earliest, latest] — for a comma list this spans the
// gaps too (use parseEventDateList() when you need the exact selected days).
export function parseEventDateRange(value) {
    if (!value)
        return null;
    const rawValue = String(value ?? '').trim();
    if (rawValue.includes(',')) {
        const dates = parseEventDateTokens(rawValue);
        if (!dates.length)
            return null;
        const start = dates.reduce((min, d) => (d.getTime() < min.getTime() ? d : min));
        const end = dates.reduce((max, d) => (d.getTime() > max.getTime() ? d : max));
        return { start, end };
    }
    const raw = normalizeEventDateRaw(rawValue);
    const parts = raw.includes(' to ') ? raw.split(' to ') : [raw, raw];
    const start = parseEventDateStart(parts[0].trim());
    const end = parseEventDateStart((parts[1] || parts[0]).trim());
    if (!start || !end)
        return null;
    return { start, end };
}
// Exact set of selected calendar days: for the new comma-list format, exactly
// the listed (possibly non-contiguous) days; for a legacy single date or
// contiguous "to" range, every day in that range inclusive (unchanged behavior).
export function parseEventDateList(value) {
    const rawValue = String(value ?? '').trim();
    if (!rawValue)
        return [];
    if (rawValue.includes(',')) {
        const dates = parseEventDateTokens(rawValue).sort((a, b) => a.getTime() - b.getTime());
        const seen = new Set();
        const unique = [];
        for (const d of dates) {
            const key = formatDateForSheet(d);
            if (!seen.has(key)) {
                seen.add(key);
                unique.push(d);
            }
        }
        return unique;
    }
    const range = parseEventDateRange(rawValue);
    if (!range)
        return [];
    const days = [];
    const cur = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate());
    const endDay = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate());
    while (cur.getTime() <= endDay.getTime()) {
        days.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return days;
}
// Window used to FIND existing Google Calendar events tagged with a JobID
// before delete/recreate. Searching only the *current* Event Date leaves
// orphans when the job is moved (or a non-contiguous day is removed and the
// min/max range shrinks). Always include ±12 months from `now`, unioned with
// every Event Date value passed in (current + previous).
export function getCalendarEventSweepRange(eventDateValues, now = new Date()) {
    let min = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate(), 0, 0, 0, 0).getTime();
    let max = new Date(now.getFullYear(), now.getMonth() + 12, now.getDate(), 23, 59, 59, 999).getTime();
    for (const value of eventDateValues) {
        const range = parseEventDateRange(value);
        if (!range)
            continue;
        const rs = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate()).getTime();
        // Pad +1 day so Google all-day events (exclusive end = next day) stay inside timeMax.
        const re = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate() + 1, 23, 59, 59, 999).getTime();
        if (rs < min)
            min = rs;
        if (re > max)
            max = re;
    }
    return { start: new Date(min), end: new Date(max) };
}
export function parseWorkingTime(value) {
    const raw = String(value ?? '').trim();
    const match = raw.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
    if (!match)
        return null;
    const startHour = Number(match[1]);
    const startMinute = Number(match[2]);
    const endHour = Number(match[3]);
    const endMinute = Number(match[4]);
    if (!Number.isFinite(startHour) ||
        !Number.isFinite(startMinute) ||
        !Number.isFinite(endHour) ||
        !Number.isFinite(endMinute) ||
        startHour < 0 ||
        startHour > 23 ||
        endHour < 0 ||
        endHour > 23 ||
        startMinute < 0 ||
        startMinute > 59 ||
        endMinute < 0 ||
        endMinute > 59) {
        return null;
    }
    return { startHour, startMinute, endHour, endMinute };
}
export function addMonths(date, months) {
    const d = new Date(date.getTime());
    d.setMonth(d.getMonth() + months);
    return d;
}
export function sameCalendarDate(left, right) {
    if (!left || !right)
        return false;
    return (left.getFullYear() === right.getFullYear() &&
        left.getMonth() === right.getMonth() &&
        left.getDate() === right.getDate());
}
export function formatDateForSheet(date) {
    if (!date || isNaN(date.getTime()))
        return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return day + '/' + month + '/' + year;
}
export function formatDateDDMMYYYY(dateStr) {
    if (!dateStr)
        return '-';
    const d = parseEventDateStart(dateStr);
    if (!d || isNaN(d.getTime()))
        return String(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return day + '/' + month + '/' + year;
}
const THAI_MONTHS_SHORT = [
    'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
    'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];
const THAI_MONTHS_FULL = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];
/** Full Thai date with Buddhist year, e.g. "21 มิถุนายน 2569" — ports v1 formatDateThaiFull_. */
export function formatDateThaiFull(dateStr) {
    if (!dateStr)
        return '-';
    const d = parseEventDateStart(dateStr);
    if (!d || isNaN(d.getTime()))
        return String(dateStr);
    return d.getDate() + ' ' + THAI_MONTHS_FULL[d.getMonth()] + ' ' + (d.getFullYear() + 543);
}
/** Day + full Thai month, no year — e.g. "12 มิถุนายน". Used for the short
 * calendar-title line appended to LINE job messages (matches v1's actual
 * output there, which is full-month/no-year — distinct from both
 * formatDateThaiFull, above, which adds the Buddhist year, and
 * formatEventDateDisplayCompact, below, which abbreviates the month). */
export function formatDateThaiMonthDay(dateStr) {
    if (!dateStr)
        return '-';
    const d = parseEventDateStart(dateStr);
    if (!d || isNaN(d.getTime()))
        return String(dateStr);
    return d.getDate() + ' ' + THAI_MONTHS_FULL[d.getMonth()];
}
/**
 * LINE footer date labels for a job's Event Date.
 * Contiguous same-month days collapse: "12-13-14 สิงหาคม".
 * Gaps (or month changes) become separate labels — one line each.
 */
export function formatDateThaiMonthDayGroups(dateStr) {
    const days = parseEventDateList(dateStr);
    if (!days.length) {
        const fallback = formatDateThaiMonthDay(dateStr);
        return fallback && fallback !== '-' ? [fallback] : [];
    }
    const groups = [];
    let current = [days[0]];
    for (let i = 1; i < days.length; i++) {
        const prev = current[current.length - 1];
        const next = days[i];
        const contiguous = next.getFullYear() === prev.getFullYear() &&
            next.getMonth() === prev.getMonth() &&
            next.getDate() === prev.getDate() + 1;
        if (contiguous) {
            current.push(next);
        }
        else {
            groups.push(current);
            current = [next];
        }
    }
    groups.push(current);
    return groups.map((group) => {
        const month = THAI_MONTHS_FULL[group[0].getMonth()];
        if (group.length === 1)
            return group[0].getDate() + ' ' + month;
        return group.map((d) => d.getDate()).join('-') + ' ' + month;
    });
}
export function formatEventDateDisplayCompact(dateStr) {
    const rawValue = String(dateStr ?? '').trim();
    if (rawValue.includes(',')) {
        const days = parseEventDateList(rawValue);
        if (!days.length)
            return String(dateStr ?? '-');
        const sameYear = days.every((d) => d.getFullYear() === days[0].getFullYear());
        const sameMonth = sameYear && days.every((d) => d.getMonth() === days[0].getMonth());
        if (sameMonth) {
            return (days.map((d) => d.getDate()).join(', ') +
                ' ' + THAI_MONTHS_SHORT[days[0].getMonth()] + ' ' + days[0].getFullYear());
        }
        if (sameYear) {
            return (days.map((d) => d.getDate() + ' ' + THAI_MONTHS_SHORT[d.getMonth()]).join(', ') +
                ' ' + days[0].getFullYear());
        }
        return days.map((d) => d.getDate() + ' ' + THAI_MONTHS_SHORT[d.getMonth()] + ' ' + d.getFullYear()).join(', ');
    }
    const range = parseEventDateRange(dateStr);
    if (!range)
        return String(dateStr ?? '-');
    const s = range.start;
    const e = range.end;
    const sameDay = s.getFullYear() === e.getFullYear() &&
        s.getMonth() === e.getMonth() &&
        s.getDate() === e.getDate();
    if (sameDay)
        return s.getDate() + ' ' + THAI_MONTHS_SHORT[s.getMonth()] + ' ' + s.getFullYear();
    const sameMonth = s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();
    if (sameMonth) {
        return s.getDate() + '-' + e.getDate() + ' ' + THAI_MONTHS_SHORT[s.getMonth()] + ' ' + s.getFullYear();
    }
    const pad = (n) => String(n).padStart(2, '0');
    return (pad(s.getDate()) + '/' + pad(s.getMonth() + 1) + '-' +
        pad(e.getDate()) + '/' + pad(e.getMonth() + 1) + '/' + s.getFullYear());
}
export function isPaymentDueManualMode(mode) {
    const raw = String(mode ?? '').trim().toLowerCase();
    return raw === 'manual' || raw === 'กำหนดเอง' || raw === 'custom';
}
// "ไม่ต้องแจ้งเตือน" — this job is intentionally excluded from payment-due
// tracking/reminders altogether (e.g. paid upfront, or no fixed due date).
export function isPaymentDueReminderDisabled(mode) {
    const raw = String(mode ?? '').trim().toLowerCase();
    return raw === 'none' || raw === 'no-reminder' || raw === 'ไม่ต้องแจ้งเตือน';
}
// Convert any event-date input to canonical "dd/MM/yyyy" or "dd/MM/yyyy to dd/MM/yyyy".
// Mirrors v1 normalizeStoredEventDate_ (timezone-independent since values are already dates).
export function normalizeStoredEventDate(value) {
    const rawValue = String(value ?? '').trim();
    if (rawValue.includes(',')) {
        const days = parseEventDateList(rawValue);
        if (!days.length)
            return normalizeEventDateRaw(rawValue);
        if (days.length === 1)
            return formatDateForSheet(days[0]);
        return days.map((d) => formatDateForSheet(d)).join(',');
    }
    const range = parseEventDateRange(value);
    if (!range)
        return normalizeEventDateRaw(value);
    const start = formatDateForSheet(range.start);
    const end = formatDateForSheet(range.end);
    if (start === end)
        return start;
    return start + ' to ' + end;
}
export function computeAutoPaymentDueDate(jobData) {
    const base = parseEventDateStart(jobData?.['Event Date']) || new Date();
    base.setHours(0, 0, 0, 0);
    return addMonths(base, PAYMENT_DUE_AUTO_MONTHS);
}
export function resolvePaymentDueFields(jobData) {
    if (!jobData)
        return jobData;
    if (isPaymentDueReminderDisabled(jobData['Payment Due Mode'])) {
        jobData['Payment Due Mode'] = 'none';
        if (jobData['Show Payment Due on Quotation'] === '' ||
            jobData['Show Payment Due on Quotation'] == null) {
            jobData['Show Payment Due on Quotation'] = true;
        }
        return jobData;
    }
    const manual = isPaymentDueManualMode(jobData['Payment Due Mode']);
    const dueRaw = String(jobData['Payment Due Date'] ?? '').trim();
    if (!dueRaw || !manual) {
        const autoDue = computeAutoPaymentDueDate(jobData);
        jobData['Payment Due Date'] = formatDateForSheet(autoDue);
        if (!manual)
            jobData['Payment Due Mode'] = 'auto';
    }
    else {
        jobData['Payment Due Mode'] = 'manual';
        const parsed = parseEventDateStart(dueRaw);
        if (parsed && !isNaN(parsed.getTime())) {
            jobData['Payment Due Date'] = formatDateForSheet(parsed);
        }
    }
    if (jobData['Show Payment Due on Quotation'] === '' ||
        jobData['Show Payment Due on Quotation'] == null) {
        jobData['Show Payment Due on Quotation'] = true;
    }
    return jobData;
}
export function ensureJobPaymentDueForUi(job) {
    if (!job)
        return job;
    if (!isAwaitingPaymentStatus(job['Status']))
        return job;
    if (isPaymentDueReminderDisabled(job['Payment Due Mode']))
        return job;
    const dueRaw = String(job['Payment Due Date'] ?? '').trim();
    if (!dueRaw) {
        const autoDue = computeAutoPaymentDueDate(job);
        job['Payment Due Date'] = formatDateForSheet(autoDue);
    }
    if (!String(job['Payment Due Mode'] ?? '').trim())
        job['Payment Due Mode'] = 'auto';
    if (job['Show Payment Due on Quotation'] === '' ||
        job['Show Payment Due on Quotation'] == null) {
        job['Show Payment Due on Quotation'] = true;
    }
    return job;
}
export function startOfDay(date) {
    const d = new Date(date.getTime());
    d.setHours(0, 0, 0, 0);
    return d;
}
export function formatDateKey(date, timeZone = 'Asia/Bangkok') {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}
export function isJobPaymentDueToday(job, today = new Date()) {
    if (isPersonalJob(job))
        return false;
    if (!isAwaitingPaymentStatus(job['Status']))
        return false;
    if (isPaymentDueReminderDisabled(job['Payment Due Mode']))
        return false;
    const due = parseEventDateStart(job['Payment Due Date']);
    if (!due)
        return false;
    return startOfDay(due).getTime() === startOfDay(today).getTime();
}
/** Due today, or overdue within PAYMENT_DUE_LINE_REMINDER_MAX_OVERDUE_MONTHS — not older debt. */
export function isJobEligibleForPaymentDueLineReminder(job, today = new Date()) {
    if (isPersonalJob(job))
        return false;
    if (!isAwaitingPaymentStatus(job['Status']))
        return false;
    if (isPaymentDueReminderDisabled(job['Payment Due Mode']))
        return false;
    const due = parseEventDateStart(job['Payment Due Date']);
    if (!due)
        return false;
    const todayDay = startOfDay(today);
    const dueDay = startOfDay(due);
    if (dueDay.getTime() === todayDay.getTime())
        return true;
    if (dueDay.getTime() > todayDay.getTime())
        return false;
    const cutoff = startOfDay(addMonths(todayDay, -PAYMENT_DUE_LINE_REMINDER_MAX_OVERDUE_MONTHS));
    return dueDay.getTime() >= cutoff.getTime();
}
export function isJobPaymentOverdue(job) {
    if (isPersonalJob(job))
        return false;
    if (!isAwaitingPaymentStatus(job['Status']))
        return false;
    if (isPaymentDueReminderDisabled(job['Payment Due Mode']))
        return false;
    const due = parseEventDateStart(job['Payment Due Date']);
    if (!due)
        return false;
    return startOfDay(due).getTime() < startOfDay(new Date()).getTime();
}
export function parseNumber(value) {
    if (value === null || value === undefined || value === '')
        return 0;
    const numberValue = Number(String(value).replace(/,/g, ''));
    return isNaN(numberValue) ? 0 : numberValue;
}
export function formatPriceForLine(value) {
    if (value === null || value === undefined || value === '')
        return '-';
    const num = Number(String(value).replace(/,/g, ''));
    if (isNaN(num))
        return String(value);
    return String(num);
}
//# sourceMappingURL=dates.js.map